import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { normalizeIssues, openIssues, urgentIssues } from './issueModel';
import brand from '../shell/brand';

const ZenyContext = createContext(null);

export const ZenyProvider = ({ children }) => {
  const [state, setState] = useState({ items: [], loading: true, error: null, updatedAt: null });
  const controller = useRef(null);
  const mounted = useRef(true);
  const refresh = useCallback(async (background = false) => {
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    const timeout = setTimeout(() => request.abort(), 15000);
    if (!background) setState((value) => ({ ...value, loading: true }));
    try {
      const response = await fetch('/attention/items', { signal: request.signal });
      if (!response.ok) throw new Error(`${brand.assistant} is unavailable (${response.status}).`);
      const items = normalizeIssues(await response.json());
      if (mounted.current && controller.current === request) {
        setState({ items, loading: false, error: null, updatedAt: new Date().toISOString() });
      }
    } catch (error) {
      if (mounted.current && controller.current === request) {
        setState((value) => ({
          ...value, loading: false, error: error.name === 'AbortError' ? `${brand.assistant} did not respond in time.` : error.message,
        }));
      }
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const timer = setInterval(() => refresh(true), 30000);
    // Named, so the same function is the one removed below.
    const onFocus = () => refresh(true);
    window.addEventListener('focus', onFocus);
    return () => {
      mounted.current = false;
      controller.current?.abort();
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const mutate = useCallback(async (id, action, body = {}) => {
    if (!['acknowledge', 'resolve'].includes(action)) throw new Error('Unsupported issue action.');
    const response = await fetch(`/attention/items/${encodeURIComponent(id)}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!response.ok) throw new Error(`Could not ${action} this issue (${response.status}). Please retry.`);
    await refresh();
  }, [refresh]);

  const value = useMemo(() => ({
    ...state,
    open: openIssues(state.items),
    urgent: urgentIssues(state.items),
    refresh,
    mutate,
  }), [state, refresh, mutate]);
  return <ZenyContext.Provider value={value}>{children}</ZenyContext.Provider>;
};

export const useZeny = () => {
  const context = useContext(ZenyContext);
  if (!context) throw new Error('ZenyProvider is required.');
  return context;
};
